/**
 * Componente para mostrar bandejas de tickets para agentes
 * 
 * Referencia: sistema-tickets-despliegue.md Sección 8.2
 */

import React, { useState, useContext, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTenantNavigation } from '../../hooks/soporte/useTenantNavigation';
import {
  Box,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  alpha
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useSoporteData } from '../../hooks/soporte/useSoporteData';
import { SoporteContext } from '../../context/SoporteContext';
import { ErpContext } from '../../context/ErpContext';
import { useTicketActions } from '../../hooks/soporte/useTicketActions';
import EstadoBadge from './shared/EstadoBadge';
import PrioridadChip from './shared/PrioridadChip';
import SLAIndicator from './shared/SLAIndicator';
import Cookies from 'js-cookie';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { 
  ESTADOS_VISIBLES, 
  LABELS_ESTADOS_VISIBLES, 
  getEstadoInternoDesdeVisible,
  getEstadoVisible
} from '../../utils/soporte/estadosMapping';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

dayjs.extend(relativeTime);
dayjs.locale('es');

// Claves para SessionStorage (filtros por tabla)
const STORAGE_KEYS = {
  BANDEJAS_FILTROS: 'soporte_bandejas_filtros',
  TABLA_FILTROS: 'soporte_tabla_filtros'
};

// Mapeo de orden para estados (para sorting consistente)
const ORDEN_ESTADOS = {
  [ESTADOS_VISIBLES.ABIERTO]: 1,
  [ESTADOS_VISIBLES.EN_PROGRESO]: 2,
  [ESTADOS_VISIBLES.COMPLETADO]: 3,
  [ESTADOS_VISIBLES.PRUEBA]: 4,
  [ESTADOS_VISIBLES.CERRADO]: 5
};

const BandejasAgenteComponent = () => {
  const navigate = useTenantNavigation();
  const { categorias, prioridades } = useContext(SoporteContext);
  const { serverURL } = useContext(ErpContext);
  
  // Obtener rol del usuario desde cookies
  const userRole = Cookies.get('rolUsuario') || '';
  const esAgente = userRole === 'Agente Soporte' || userRole === 'Soporte';
  const esAdmin = userRole === 'Administrador';
  
  // Estado de filtro activo (bandeja seleccionada)
  const [bandejaActiva, setBandejaActiva] = useState(null);
  
  // Funciones para manejar SessionStorage
  const guardarFiltrosEnStorage = useCallback((filtrosActuales) => {
    try {
      sessionStorage.setItem(
        STORAGE_KEYS.BANDEJAS_FILTROS,
        JSON.stringify(filtrosActuales)
      );
      console.log('💾 [BandejasAgenteComponent] Filtros guardados en SessionStorage:', filtrosActuales);
    } catch (error) {
      console.error('❌ [BandejasAgenteComponent] Error guardando filtros en SessionStorage:', error);
    }
  }, []);
  
  const cargarFiltrosDesdeStorage = useCallback(() => {
    try {
      const filtrosGuardados = sessionStorage.getItem(STORAGE_KEYS.BANDEJAS_FILTROS);
      if (filtrosGuardados) {
        const parsed = JSON.parse(filtrosGuardados);
        console.log('📂 [BandejasAgenteComponent] Filtros cargados desde SessionStorage:', parsed);
        return parsed;
      }
    } catch (error) {
      console.error('❌ [BandejasAgenteComponent] Error cargando filtros desde SessionStorage:', error);
    }
    return null;
  }, []);
  
  const limpiarFiltrosStorage = useCallback(() => {
    try {
      sessionStorage.removeItem(STORAGE_KEYS.BANDEJAS_FILTROS);
      console.log('🧹 [BandejasAgenteComponent] Filtros eliminados de SessionStorage');
    } catch (error) {
      console.error('❌ [BandejasAgenteComponent] Error eliminando filtros de SessionStorage:', error);
    }
  }, []);
  
  // Estado de filtros adicionales - cargar desde SessionStorage si existe
  const [filtros, setFiltros] = useState(() => {
    const filtrosGuardados = cargarFiltrosDesdeStorage();
    return filtrosGuardados || {
      search: '',
      prioridad: '',
      categoria: '',
      estado: '',
      usuario: '',
      creador: ''
    };
  });
  
  // Estado de paginación - 100 filas por página (fijo)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100
  });

  // Estado de sorting
  const [sortModel, setSortModel] = useState([{ field: 'updatedAt', sort: 'desc' }]);
  
  // Cargar métricas para las cards
  const { data: metricsData, loading: metricsLoading } = useSoporteData('/metrics/cards');
  const metrics = metricsData?.metrics || {};
  
  // Estado para lista de usuarios
  const [usuarios, setUsuarios] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  
  // Cargar lista de usuarios
  useEffect(() => {
    const cargarUsuarios = async () => {
      if (!esAgente && !esAdmin) {
        // Usuarios normales no necesitan filtro de usuario
        return;
      }
      
      setLoadingUsuarios(true);
      try {
        const response = await axios.get(`${serverURL}/adminAPI/getUsuario`, {
          withCredentials: true
        });
        
        // Filtrar usuarios activos y no eliminados
        const usuariosActivos = (response.data || []).filter(u => 
          !u.eliminado || u.eliminado === false
        );
        
        // Ordenar por nombre
        usuariosActivos.sort((a, b) => {
          const nombreA = (a.nombre || '').toLowerCase();
          const nombreB = (b.nombre || '').toLowerCase();
          return nombreA.localeCompare(nombreB);
        });
        
        setUsuarios(usuariosActivos);
      } catch (error) {
        console.error('Error cargando usuarios:', error);
      } finally {
        setLoadingUsuarios(false);
      }
    };
    
    cargarUsuarios();
  }, [serverURL, esAgente, esAdmin]);
  
  // Construir parámetros para la API basado en bandeja activa y filtros
  const params = useMemo(() => {
    const queryParams = {
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      sort: 'updatedAt',
      order: 'DESC'
    };

    // Aplicar sorting si existe
    // Nota: el campo 'status' se ordena client-side porque múltiples estados internos
    // mapean al mismo estado visible, por lo que el orden alphabético del backend
    // no corresponde al orden lógico que ve el usuario.
    if (sortModel.length > 0) {
      const sortItem = sortModel[0];
      if (sortItem.field !== 'status') {
        queryParams.sort = sortItem.field;
        queryParams.order = sortItem.sort;
      }
    }
    
    console.log('🔍 [BandejasAgenteComponent] bandejaActiva:', bandejaActiva);
    
    // Aplicar filtro de bandeja activa
    // NOTA: Las bandejas muestran TODOS los tickets del sistema, excepto "mis_tickets" que muestra solo los asignados al agente actual
    if (bandejaActiva === 'tickets_totales') {
      // Tickets Totales: mostrar todos los tickets activos (no cerrados, no resueltos)
      // Excluir estados cerrado y resuelto usando estados internos directamente
      queryParams.status = 'nuevo,abierto,en_progreso,esperando_cliente,en_espera,pendiente_validacion,pendiente_validacion_nube';
    } else if (bandejaActiva === 'sin_asignar') {
      queryParams.unassigned = 'true';
      queryParams.status = 'nuevo';
    } else if (bandejaActiva === 'mis_tickets') {
      // Tickets asignados AL usuario Y tickets creados POR el usuario
      queryParams.assignedToMe = 'true';
      queryParams.includeClosed = 'false'; // Excluir tickets cerrados
    } else if (bandejaActiva === 'nuevos') {
      queryParams.status = 'nuevo';
      queryParams.nuevos_hoy = 'true'; // ✅ Indicar que debe filtrar por fecha (creados HOY)
      // No agregar assignedToMe para mostrar TODOS los tickets nuevos
    } else if (bandejaActiva === 'en_progreso') {
      queryParams.status = 'en_progreso';
      // No agregar assignedToMe para mostrar TODOS los tickets en progreso
    } else if (bandejaActiva === 'esperando_cliente') {
      queryParams.status = 'esperando_cliente';
      // No agregar assignedToMe para mostrar TODOS los tickets esperando cliente
    } else if (bandejaActiva === 'sla_vencido') {
      // Los tickets con SLA vencido se filtran por firstResponseViolated o resolutionViolated
      // Esto requiere una query especial, por ahora usamos un filtro de búsqueda
      queryParams.status = 'nuevo,en_progreso,esperando_cliente';
      // No agregar assignedToMe para mostrar TODOS los tickets con SLA vencido
    } else if (bandejaActiva === 'todos') {
      // No aplicar ningún filtro de estado o asignación para mostrar todos
    }
    
    // Agregar filtros adicionales si existen
    if (filtros.search && filtros.search.trim()) {
      queryParams.search = filtros.search.trim();
    }
    
    if (filtros.prioridad && filtros.prioridad !== '') {
      queryParams.prioridad = filtros.prioridad;
    }
    
    if (filtros.categoria && filtros.categoria !== '') {
      queryParams.categoria = filtros.categoria;
    }
    
    if (filtros.estado && filtros.estado !== '') {
      queryParams.status = filtros.estado;
    }
    
    // Agregar filtro de agente asignado (solo para agentes y administradores)
    if (filtros.usuario && filtros.usuario !== '' && (esAgente || esAdmin)) {
      queryParams.agent = filtros.usuario;
    }

    // Agregar filtro de creador del ticket (solo para agentes y administradores)
    if (filtros.creador && filtros.creador !== '' && (esAgente || esAdmin)) {
      queryParams.requester = filtros.creador;
    }
    
    console.log('🔍 [BandejasAgenteComponent] Parámetros construidos:', queryParams);
    console.log('🔍 [BandejasAgenteComponent] Filtros actuales:', filtros);
    
    return queryParams;
  }, [bandejaActiva, filtros, paginationModel, sortModel]);
  
  // Cargar tickets
  const { data, loading, error, reload } = useSoporteData('/tickets', params);
  
  // Hook para acciones de tickets
  const { cambiarEstado, cambiarEstadoMasivo, loading: actionsLoading, error: actionsError } = useTicketActions();
  
  // Estado para dialog de cambiar estado
  const [mostrarCambiarEstado, setMostrarCambiarEstado] = useState(false);
  const [ticketSeleccionado, setTicketSeleccionado] = useState(null);
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [errorAccion, setErrorAccion] = useState('');
  
  // Estado para selección múltiple de tickets
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [mostrarCambiarEstadoMasivo, setMostrarCambiarEstadoMasivo] = useState(false);
  const [nuevoEstadoMasivo, setNuevoEstadoMasivo] = useState('');
  const [cambiandoEstadoMasivo, setCambiandoEstadoMasivo] = useState(false);
  const [errorCambioMasivo, setErrorCambioMasivo] = useState('');
  
  // Extraer tickets y paginación
  const ticketsRaw = data?.tickets || [];
  const pagination = data?.pagination || { total: 0, pages: 0 };

  // Ordenamiento client-side para la columna 'status'
  // El backend no puede ordenar lógicamente porque varios estados internos
  // mapean al mismo estado visible (ej: resuelto y cerrado → "Cerrado")
  const tickets = useMemo(() => {
    if (sortModel.length > 0 && sortModel[0].field === 'status') {
      const sortDir = sortModel[0].sort === 'asc' ? 1 : -1;
      return [...ticketsRaw].sort((a, b) => {
        const ordenA = ORDEN_ESTADOS[getEstadoVisible(a.status)] || 0;
        const ordenB = ORDEN_ESTADOS[getEstadoVisible(b.status)] || 0;
        return (ordenA - ordenB) * sortDir;
      });
    }
    return ticketsRaw;
  }, [ticketsRaw, sortModel]);
  
  /**
   * Manejar click en card de bandeja
   */
  const handleBandejaClick = (bandejaId) => {
    if (bandejaActiva === bandejaId) {
      // Si ya está activa, desactivar (mostrar todos)
      setBandejaActiva(null);
    } else {
      setBandejaActiva(bandejaId);
    }
    // Resetear a primera página y limpiar selección
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setSelectedTickets([]);
  };
  
  /**
   * Manejar cambio en filtros
   */
  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    setFiltros(prev => {
      const nuevosFiltros = {
        ...prev,
        [field]: value
      };
      // Guardar en SessionStorage después de actualizar
      setTimeout(() => guardarFiltrosEnStorage(nuevosFiltros), 0);
      return nuevosFiltros;
    });
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setSelectedTickets([]); // Limpiar selección al cambiar filtros
  };
  
  /**
   * Limpiar un filtro específico
   */
  const handleLimpiarFiltro = (filtroKey) => {
    setFiltros(prev => {
      const nuevosFiltros = {
        ...prev,
        [filtroKey]: ''
      };
      // Guardar en SessionStorage después de actualizar
      setTimeout(() => guardarFiltrosEnStorage(nuevosFiltros), 0);
      return nuevosFiltros;
    });
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setSelectedTickets([]);
    
    console.log(`🧹 [BandejasAgenteComponent] Filtro ${filtroKey} eliminado`);
  };

  /**
   * Limpiar todos los filtros
   */
  const handleLimpiarFiltros = () => {
    const filtrosIniciales = {
      search: '',
      prioridad: '',
      categoria: '',
      estado: '',
      usuario: '',
      creador: ''
    };
    
    setFiltros(filtrosIniciales);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setSelectedTickets([]);
    setBandejaActiva(null);
    limpiarFiltrosStorage();
    
    console.log('🧹 [BandejasAgenteComponent] Todos los filtros han sido limpiados');
  };
  
  /**
   * Manejar click en fila
   */
  const handleRowClick = (params, event) => {
    // Solo navegar si no se hizo click en un checkbox
    if (!event.target.closest('.MuiCheckbox-root') && !event.target.closest('.MuiDataGrid-cellCheckbox')) {
      // Guardar origen de navegación: 'bandejas' para Mis Tickets
      sessionStorage.setItem('ticket_navegacion_origen', 'bandejas');
      console.log("ccc",sessionStorage.getItem('ticket_navegacion_origen'));
      if (params?.row?.id) {
        navigate(`/soporte/ticket/${params.row.id}`);
      }
    }
  };
  
  /**
   * Manejar click en estado (solo para agentes y administradores)
   */
  const handleEstadoClick = (event, ticket) => {
    event.stopPropagation(); // Prevenir que se active el click en la fila
    
    if (esAgente || esAdmin) {
      setTicketSeleccionado(ticket);
      setNuevoEstado('');
      setErrorAccion('');
      setMostrarCambiarEstado(true);
    }
  };
  
  /**
   * Manejar aplicar cambio de estado
   */
  const handleAplicarCambioEstado = async () => {
    if (!nuevoEstado || !ticketSeleccionado) return;
    
    setErrorAccion('');
    
    // Mapear el estado visible seleccionado al estado interno
    const estadoInterno = getEstadoInternoDesdeVisible(nuevoEstado, ticketSeleccionado.status);
    
    const exito = await cambiarEstado(ticketSeleccionado.id, estadoInterno);
    if (exito) {
      setMostrarCambiarEstado(false);
      setTicketSeleccionado(null);
      setNuevoEstado('');
      setErrorAccion('');
      reload(); // Recargar la tabla
    } else {
      const mensajeError = actionsError || 'No puedes cambiar el estado del ticket';
      setErrorAccion(mensajeError);
    }
  };
  
  /**
   * Manejar cambio en selección de tickets
   */
  const handleSelectionChange = (newSelection) => {
    setSelectedTickets(newSelection);
  };
  
  /**
   * Manejar abrir dialog para cambio masivo de estado
   */
  const handleAbrirCambioEstadoMasivo = () => {
    if (selectedTickets.length === 0) return;
    
    setNuevoEstadoMasivo('');
    setErrorCambioMasivo('');
    setMostrarCambiarEstadoMasivo(true);
  };
  
  /**
   * Manejar aplicar cambio de estado masivo
   */
  const handleAplicarCambioEstadoMasivo = async () => {
    if (!nuevoEstadoMasivo || selectedTickets.length === 0) return;
    
    setCambiandoEstadoMasivo(true);
    setErrorCambioMasivo('');
    
    try {
      // Obtener los tickets seleccionados de la lista actual
      const ticketsSeleccionados = tickets.filter(ticket => selectedTickets.includes(ticket.id));
      
      // Validar que el usuario tenga permiso para cambiar el estado de todos los tickets
      const ticketsConPermiso = ticketsSeleccionados.filter(ticket => {
        // Agentes solo pueden cambiar tickets asignados a ellos
        if (esAgente && !esAdmin) {
          return ticket.agentId === Cookies.get('userId');
        }
        // Administradores pueden cambiar cualquier ticket
        return true;
      });
      
      if (ticketsConPermiso.length !== ticketsSeleccionados.length) {
        setErrorCambioMasivo(`No tienes permiso para cambiar el estado de ${ticketsSeleccionados.length - ticketsConPermiso.length} tickets`);
        setCambiandoEstadoMasivo(false);
        return;
      }
      
      // Obtener el estado interno para el cambio masivo
      // Usamos el estado del primer ticket como referencia para el mapeo
      const estadoReferencia = ticketsSeleccionados[0]?.status;
      const estadoInterno = getEstadoInternoDesdeVisible(nuevoEstadoMasivo, estadoReferencia);
      
      // Cambiar estado de todos los tickets de forma masiva
      const resultados = await cambiarEstadoMasivo(selectedTickets, estadoInterno);
      
      // Mostrar resultados
      if (resultados.fallos > 0) {
        setErrorCambioMasivo(
          `Se cambiaron ${resultados.exitos} tickets exitosamente. Fallaron ${resultados.fallos} tickets:\n${resultados.errores.join('\n')}`
        );
        
        // Si hubo algún éxito, actualizar la tabla
        if (resultados.exitos > 0) {
          reload();
        }
      } else {
        setMostrarCambiarEstadoMasivo(false);
        setNuevoEstadoMasivo('');
        setSelectedTickets([]);
        reload(); // Recargar la tabla
      }
    } catch (error) {
      setErrorCambioMasivo(`Error al procesar el cambio masivo: ${error.message}`);
    } finally {
      setCambiandoEstadoMasivo(false);
    }
  };
  
  /**
   * Definir columnas del DataGrid
   * ✅ Actualización es la primera columna (última actualización del ticket)
   * ✅ Textos aumentados 30% (de 0.875rem a 1.1375rem)
   * ✅ Centrado vertical en todas las celdas
   * ✅ "Agente" cambiado a "Usuario"
   */
  const columns = [
    {
      field: 'updatedAt',
      headerName: 'Actualización',
      width: 180,
      align: 'left',
      headerAlign: 'left',
      sortable: true,
      renderCell: (params) => {
        const fecha = params.value ? dayjs(params.value) : null;
        // ✅ Reducido 5% (de 1.1375rem a 1.0806rem)
        return fecha ? (
          <Typography variant="body2" sx={{ fontSize: '1.0806rem' }}>
            {fecha.format('DD/MM/YYYY HH:mm')}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.0806rem' }}>
            N/A
          </Typography>
        );
      }
    },
    {
      field: 'createdAt',
      headerName: 'Fecha',
      width: 180,
      align: 'left',
      headerAlign: 'left',
      sortable: true,
      renderCell: (params) => {
        const fecha = params.value ? dayjs(params.value) : null;
        // ✅ Reducido 5% (de 1.1375rem a 1.0806rem)
        return fecha ? (
          <Typography variant="body2" sx={{ fontSize: '1.0806rem' }}>
            {fecha.format('DD/MM/YYYY HH:mm')}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.0806rem' }}>
            N/A
          </Typography>
        );
      }
    },
    {
      field: 'numero',
      headerName: 'Número',
      width: 182, // ✅ Aumentado 30% (140 * 1.30 = 182)
      align: 'left',
      headerAlign: 'left',
      sortable: true,
      renderCell: (params) => (
        // ✅ Reducido 5% (de 1.1375rem a 1.0806rem)
        <Chip
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontSize: '1.0806rem' }}
        />
      )
    },
    {
      field: 'titulo',
      headerName: 'Título',
      flex: 0.65, // ✅ Reducido para compensar nueva columna Actualización (de 0.72 a 0.65)
      minWidth: 130, // ✅ Reducido para compensar nueva columna Actualización (de 150 a 130)
      align: 'left',
      headerAlign: 'left',
      sortable: true,
      renderCell: (params) => {
        const descripcion = params.row.descripcion || '';
        const tieneDescripcion = descripcion && descripcion.trim() !== '';
        
        return (
          <Tooltip
            title={
              <Typography variant="body2" sx={{ fontSize: '1.2rem', lineHeight: 1.4 }}>
                {tieneDescripcion ? descripcion : 'Sin descripción'}
              </Typography>
            }
            placement="top-start"
            enterDelay={1000} // 1 segundo de delay
            leaveDelay={200}
            arrow
            disableHoverListener={!tieneDescripcion}
          >
            <Typography variant="body2" noWrap sx={{ fontSize: '1.1375rem' }}>
              {params.value}
            </Typography>
          </Tooltip>
        );
      }
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 165,
      align: 'left',
      headerAlign: 'left',
      sortable: true,
      valueGetter: (params) => {
        if (!params?.row) return '';
        const estadoInterno = params.row.status;
        const estadoVisible = getEstadoVisible(estadoInterno);
        const tieneAgente = params.row.agentId && (params.row.Agent || params.row.agent);
        
        // Para sorting, usar el orden numérico del estado visible
        let orden = ORDEN_ESTADOS[estadoVisible] || 0;
        
        // Si es estado ABIERTO, ajustar orden según si tiene agente asignado
        if (estadoVisible === ESTADOS_VISIBLES.ABIERTO) {
          // ABIERTO con agente: orden 1.0
          // ABIERTO sin agente: orden 1.5 (entre ABIERTO asignado y EN_PROGRESO)
          orden = tieneAgente ? 1.0 : 1.5;
        }
        
        return orden;
      },
      renderCell: (params) => (
        // ✅ Mismo tamaño que número (1.0806rem)
        // ✅ Clickable solo para agentes y administradores
        <Box
          onClick={(e) => params?.row && handleEstadoClick(e, params.row)}
          sx={{
            cursor: (esAgente || esAdmin) ? 'pointer' : 'default',
            '&:hover': (esAgente || esAdmin) ? {
              opacity: 0.8,
              transform: 'scale(1.05)',
              transition: 'all 0.2s'
            } : {}
          }}
        >
          <EstadoBadge status={params.row.status} fontSize="1.0806rem" />
        </Box>
      )
    },
    {
      field: 'prioridad',
      headerName: 'Prioridad',
      width: 150,
      align: 'left',
      headerAlign: 'left',
      sortable: true,
      valueGetter: (params) => {
        // Para sorting, usar el nombre de la prioridad
        if (!params?.row) return '';
        const prioridadObj = params.row.Prioridad || params.row.prioridad;
        return prioridadObj?.nombre || '';
      },
      renderCell: (params) => (
        // ✅ Mismo tamaño que número (1.0806rem)
        <PrioridadChip prioridad={params?.row?.Prioridad || params?.row?.prioridad} fontSize="1.0806rem" />
      )
    },
    {
      field: 'categoria',
      headerName: 'Categoría',
      width: 165, // ✅ Aumentado para compensar reducción de título
      align: 'left',
      headerAlign: 'left',
      sortable: true,
      valueGetter: (params) => {
        // Para sorting, usar el nombre de la categoría
        if (!params?.row) return '';
        const categoriaObj = params.row.Categoria || params.row.categoria;
        return categoriaObj?.nombre || '';
      },
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '1.1375rem' }}>
          {params?.row?.Categoria?.nombre || params?.row?.categoria?.nombre || 'N/A'}
        </Typography>
      )
    },
    {
      field: 'sla',
      headerName: 'SLA',
      width: 120,
      hide: true, // ✅ Ocultar visualmente pero mantener funcionalidad
      renderCell: (params) => (
        <SLAIndicator ticket={params?.row} />
      )
    },
    {
      field: 'agent',
      headerName: 'Usuario', // ✅ Cambiado de "Agente" a "Usuario"
      width: 150,
      align: 'left',
      headerAlign: 'left',
      sortable: true,
      valueGetter: (params) => {
        if (!params?.row) return 'zzzzzzzzzz'; // Colocar usuarios sin asignar al final
        const agente = params.row.Agent || params.row.agent;
        if (agente) {
          // Para sorting consistente, usar siempre el mismo formato: "Apellido, Nombre (usuario)"
          const nombre = agente.nombre || '';
          const apellido = agente.apellido || '';
          const usuario = agente.usuario || agente.nombreUsuario || '';
          
          if (apellido && nombre) {
            return `${apellido}, ${nombre} (${usuario})`;
          } else if (nombre) {
            return `${nombre} (${usuario})`;
          } else if (usuario) {
            return usuario;
          }
          return 'zzzzzzzzzz';
        }
        return 'zzzzzzzzzz'; // Usuarios sin asignar al final
      },
      renderCell: (params) => {
        if (!params?.row) return 'N/A';
        const agente = params.row.Agent || params.row.agent;
        if (params.row.agentId && agente) {
          // Mostrar nombre completo o nombreUsuario
          const nombreCompleto = agente.nombreCompleto || 
                                (agente.nombre && agente.apellido ? `${agente.nombre} ${agente.apellido}`.trim() : null) ||
                                agente.nombreUsuario || 
                                agente.nombre || 
                                'N/A';
          return (
            <Typography variant="body2" sx={{ fontSize: '1.1375rem' }}>
              {nombreCompleto}
            </Typography>
          );
        }
        return (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.1375rem' }}>
            Sin asignar
          </Typography>
        );
      }
    }
  ];
  
   // Definir bandejas con sus configuraciones
  const bandejas = [
    {
      id: 'tickets_totales',
      titulo: 'Tickets Totales',
      contador: metrics.total || 0, // ✅ Todos los tickets en estado NO CERRADO (resuelto o cerrado)
      color: '#2e7d32',
      bgcolor: '#e8f5e9'
    },
    {
      id: 'sin_asignar',
      titulo: 'Sin Asignar',
      contador: metrics.sinAsignar || 0,
      color: '#f44336',
      bgcolor: '#ffebee'
    },
    {
      id: 'mis_tickets',
      titulo: 'Mis Tickets',
      contador: metrics.misTickets || 0, // ✅ Nueva métrica con lógica compleja
      color: '#2196f3',
      bgcolor: '#e3f2fd'
    },
    {
      id: 'nuevos',
      titulo: 'Nuevos',
      contador: metrics.nuevos || 0, // ✅ Tickets creados HOY (cualquier estado)
      color: '#ff9800',
      bgcolor: '#fff3e0'
    },
    {
      id: 'en_progreso',
      titulo: 'En Progreso',
      contador: metrics.porEstado?.en_progreso || 0,
      color: '#1976d2',
      bgcolor: '#e3f2fd'
    },
    {
      id: 'esperando_cliente',
      titulo: 'Esperando Cliente',
      contador: metrics.esperandoCliente || 0, // ✅ Incluye esperando_cliente, pendiente_validacion y pendiente_validacion_nube
      color: '#9c27b0',
      bgcolor: '#f3e5f5'
    },
    // {
    //   id: 'sla_vencido',
    //   titulo: 'SLA Vencido',
    //   contador: '-',//metrics.slaViolado || 0,
    //   color: '#d32f2f',
    //   bgcolor: '#ffebee'
    // }
  ];
  
  return (
    <Box sx={{ p: 3 ,caretColor: 'transparent'}}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        TICKETS
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Gestiona tus tickets de soporte desde aquí
      </Typography>
      
      {/* Cards de Bandejas */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {bandejas.map((bandeja) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={bandeja.id}>
            <Card
              elevation={bandejaActiva === bandeja.id ? 4 : 2}
              sx={{
                bgcolor: bandejaActiva === bandeja.id ? bandeja.bgcolor : 'background.paper',
                border: bandejaActiva === bandeja.id ? `2px solid ${bandeja.color}` : 'none',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => handleBandejaClick(bandeja.id)}
            >
              <CardActionArea>
                <CardContent>
                  <Typography 
                    variant="h4" 
                    color={bandeja.color} 
                    fontWeight="bold"
                    sx={{ fontSize: '3.1875rem' }} // ✅ Aumentado 50% (de 2.125rem a 3.1875rem)
                  >
                    {metricsLoading ? '...' : bandeja.contador}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    mt={1}
                    sx={{ fontSize: '1.3125rem' }} // ✅ Aumentado 50% (de 0.875rem a 1.3125rem)
                  >
                    {bandeja.titulo}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
      
       {/* Filtros Adicionales */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
         <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
           <Typography variant="subtitle2">
             Filtros Adicionales
           </Typography>
           
            <Stack direction="row" spacing={1}>
              {/* Botón para cambio masivo de estado (solo para agentes y administradores) */}
              {(esAgente || esAdmin) && selectedTickets.length > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleAbrirCambioEstadoMasivo}
                >
                  Cambiar Estado ({selectedTickets.length})
                </Button>
              )}
            </Stack>
         </Stack>
        
          <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" sx={{ '& > *': { my: 0.5 } }}>
            {/* Búsqueda */}
           <TextField
             label="Buscar"
             placeholder="Buscar por número, título..."
             value={filtros.search}
             onChange={handleFilterChange('search')}
             size="small"
             sx={{ minWidth: 200 }}
           />
           
           {/* Estado */}
           <FormControl size="small" sx={{ minWidth: 180 }}>
             <InputLabel>Estado</InputLabel>
             <Select
               value={filtros.estado}
               onChange={handleFilterChange('estado')}
               label="Estado"
             >
               <MenuItem value="">Todos</MenuItem>
               <MenuItem value={ESTADOS_VISIBLES.ABIERTO}>
                 {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.ABIERTO]}
               </MenuItem>
               <MenuItem value={ESTADOS_VISIBLES.EN_PROGRESO}>
                 {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.EN_PROGRESO]}
               </MenuItem>
               <MenuItem value={ESTADOS_VISIBLES.COMPLETADO}>
                 {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.COMPLETADO]}
               </MenuItem>
               <MenuItem value={ESTADOS_VISIBLES.PRUEBA}>
                 {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.PRUEBA]}
               </MenuItem>
               <MenuItem value={ESTADOS_VISIBLES.CERRADO}>
                 {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.CERRADO]}
               </MenuItem>
             </Select>
           </FormControl>
           
           {/* Prioridad */}
           <FormControl size="small" sx={{ minWidth: 180 }}>
             <InputLabel>Prioridad</InputLabel>
             <Select
               value={filtros.prioridad}
               onChange={handleFilterChange('prioridad')}
               label="Prioridad"
             >
               <MenuItem value="">Todas</MenuItem>
               {prioridades
                 .filter(p => p.activa && !p.eliminado)
                 .sort((a, b) => (a.orden || 0) - (b.orden || 0))
                 .map(prioridad => (
                   <MenuItem key={prioridad.id} value={prioridad.nombre}>
                     {prioridad.nombre}
                   </MenuItem>
                 ))}
             </Select>
           </FormControl>
           
           {/* Categoría */}
           <FormControl size="small" sx={{ minWidth: 180 }}>
             <InputLabel>Categoría</InputLabel>
             <Select
               value={filtros.categoria}
               onChange={handleFilterChange('categoria')}
               label="Categoría"
             >
               <MenuItem value="">Todas</MenuItem>
               {categorias
                 .filter(c => c.activa && !c.eliminado)
                 .sort((a, b) => (a.orden || 0) - (b.orden || 0))
                 .map(categoria => (
                   <MenuItem key={categoria.id} value={categoria.id}>
                     {categoria.nombre}
                   </MenuItem>
                 ))}
             </Select>
           </FormControl>
           
           {/* Agente Asignado (solo para agentes y administradores) */}
           {(esAgente || esAdmin) && (
             <FormControl size="small" sx={{ minWidth: 200 }}>
               <InputLabel>Usuario Asignado</InputLabel>
               <Select
                 value={filtros.usuario}
                 onChange={handleFilterChange('usuario')}
                 label="Agente Asignado"
                 disabled={loadingUsuarios}
               >
                <MenuItem value="">Todos</MenuItem>
                {usuarios.map(usuario => (
                  <MenuItem key={usuario.id} value={usuario.id}>
                    {usuario.nombre} {usuario.apellido} ({usuario.usuario})
                  </MenuItem>
                ))}
               </Select>
             </FormControl>
           )}

           {/* Creador del Ticket (solo para agentes y administradores) */}
           {(esAgente || esAdmin) && (
             <FormControl size="small" sx={{ minWidth: 200 }}>
               <InputLabel>Creador del Ticket</InputLabel>
               <Select
                 value={filtros.creador}
                 onChange={handleFilterChange('creador')}
                 label="Creador del Ticket"
                 disabled={loadingUsuarios}
               >
                 <MenuItem value="">Todos</MenuItem>
                 {usuarios.map(usuario => (
                   <MenuItem key={usuario.id} value={usuario.id}>
                     {usuario.nombre} {usuario.apellido} ({usuario.usuario})
                   </MenuItem>
                 ))}
               </Select>
             </FormControl>
           )}

            {/* Área para chips de filtros activos (a la derecha del último filtro) */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ my: 0.5, '& > *': { my: 0.5 } }}>
              {/* Filtro de búsqueda */}
             {filtros.search && filtros.search.trim() && (
               <Chip
                 label={`Buscar: "${filtros.search}"`}
                 onDelete={() => handleLimpiarFiltro('search')}
                 color="primary"
                 variant="outlined"
                 size="small"
               />
             )}
             
             {/* Filtro de estado */}
             {filtros.estado && filtros.estado !== '' && (
               <Chip
                 label={`Estado: ${LABELS_ESTADOS_VISIBLES[filtros.estado] || filtros.estado}`}
                 onDelete={() => handleLimpiarFiltro('estado')}
                 color="primary"
                 variant="outlined"
                 size="small"
               />
             )}
             
             {/* Filtro de prioridad */}
             {filtros.prioridad && filtros.prioridad !== '' && (
               <Chip
                 label={`Prioridad: ${filtros.prioridad}`}
                 onDelete={() => handleLimpiarFiltro('prioridad')}
                 color="primary"
                 variant="outlined"
                 size="small"
               />
             )}
             
             {/* Filtro de categoría */}
             {filtros.categoria && filtros.categoria !== '' && (
               <Chip
                 label={`Categoría: ${categorias.find(c => c.id === filtros.categoria)?.nombre || filtros.categoria}`}
                 onDelete={() => handleLimpiarFiltro('categoria')}
                 color="primary"
                 variant="outlined"
                 size="small"
               />
             )}
             
             {/* Filtro de usuario asignado */}
             {filtros.usuario && filtros.usuario !== '' && (esAgente || esAdmin) && (
               <Chip
                 label={`Usuario Asignado: ${usuarios.find(u => u.id === filtros.usuario)?.usuario || filtros.usuario}`}
                 onDelete={() => handleLimpiarFiltro('usuario')}
                 color="primary"
                 variant="outlined"
                 size="small"
               />
             )}
             
             {/* Filtro de creador del ticket */}
             {filtros.creador && filtros.creador !== '' && (esAgente || esAdmin) && (
               <Chip
                 label={`Creador: ${usuarios.find(u => u.id === filtros.creador)?.usuario || filtros.creador}`}
                 onDelete={() => handleLimpiarFiltro('creador')}
                 color="primary"
                 variant="outlined"
                 size="small"
               />
             )}
             
             {/* Filtro de bandeja activa */}
             {bandejaActiva && (
               <Chip
                 label={`Bandeja: ${bandejas.find(b => b.id === bandejaActiva)?.titulo || bandejaActiva}`}
                 onDelete={() => handleBandejaClick(bandejaActiva)}
                 color="primary"
                 variant="outlined"
                 size="small"
               />
             )}
             
             {/* Botón para eliminar todos los filtros (solo si hay al menos un filtro activo) */}
             {(filtros.search || filtros.estado || filtros.prioridad || filtros.categoria || filtros.usuario || filtros.creador || bandejaActiva) && (
               <Tooltip title="Eliminar todos los filtros">
                 <IconButton
                   onClick={handleLimpiarFiltros}
                   size="small"
                   sx={{
                     color: 'error.main',
                     backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
                     borderRadius: 1.5,
                     p: 1,
                     '&:hover': {
                       backgroundColor: (theme) => alpha(theme.palette.error.main, 0.15),
                     },
                     transition: 'all 0.2s ease',
                   }}
                 >
                   <FilterAltOffIcon fontSize="small" />
                 </IconButton>
               </Tooltip>
             )}
           </Stack>
         </Stack>
      </Paper>
      
      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => reload()}>
          {error}
        </Alert>
      )}
      
       {/* DataGrid */}
       <Paper elevation={2}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
            <DataGrid
               rows={tickets}
               columns={columns}
               getRowId={(row) => row.id}
               paginationModel={paginationModel}
               onPaginationModelChange={setPaginationModel}
               rowCount={pagination.total || 0}
               paginationMode="server"
               loading={loading}
               onRowClick={handleRowClick}
               columnVisibilityModel={{ sla: false }} // ✅ Forzar ocultación de columna SLA
               disableColumnMenu={false} // Mantener menú pero columna SLA oculta
               
               // Configuración para sorting
               sortingMode="server"
               sortModel={sortModel}
               onSortModelChange={(newSortModel) => {
                 setSortModel(newSortModel);
                 // Resetear a primera página al cambiar sorting
                 setPaginationModel(prev => ({ ...prev, page: 0 }));
               }}
               
               // Configuración para selección múltiple
               checkboxSelection={(esAgente || esAdmin)} // Solo habilitar checkboxes para agentes y administradores
               rowSelectionModel={selectedTickets}
               onRowSelectionModelChange={handleSelectionChange}
               disableRowSelectionOnClick={false} // Permitir selección al hacer click en la fila
               
               // Altura fija y scroll
               autoHeight={false}
             sx={{
               width: '100%',
               height: 'calc(100vh - 500px)',
               border: 0,
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#5b5b5f',
                color: 'white',
                borderBottom: '2px solid #333',
                height: '45px !important',
                minHeight: '45px !important',
                maxHeight: '45px !important',
                padding: '0 8px',
              },
              '& .MuiDataGrid-columnHeader': {
                height: '45px !important',
                minHeight: '45px !important',
                maxHeight: '45px !important',
                padding: '0 8px',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: 'white !important',
                lineHeight: '45px !important',
                fontSize: '1.1375rem', // ✅ Aumentado 30% (de 0.875rem)
                fontWeight: 600,
              },
               '& .MuiDataGrid-columnHeaderTitleContainer': {
                 padding: '0 8px',
               },
               // Estilos para centrar el checkbox del header
               '& .MuiDataGrid-columnHeaderCheckbox': {
                 padding: '0 !important',
                 margin: '0 !important',
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 width: '100%',
                 height: '100%',
               },
               '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root': {
                 padding: '8px !important',
                 margin: '0 !important',
               },
               '& .MuiDataGrid-menuIconButton': {
                color: 'rgba(255, 255, 255, 0.7) !important',
                '&:hover': {
                  color: 'white !important',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              },
              '& .MuiDataGrid-iconButtonContainer': {
                color: 'white !important',
              },
               '& .MuiDataGrid-cell': {
                 borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                 padding: '0 16px',
                 fontSize: '1.1375rem', // ✅ Aumentado 30% (de 0.875rem)
                 display: 'flex',
                 alignItems: 'center', // ✅ Centrado vertical
                 justifyContent: 'flex-start', // ✅ Alineado a la izquierda
               },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:nth-of-type(even)': {
                  backgroundColor: '#F7F7F7',
                },
                '&:hover': {
                  backgroundColor: '#E3EAFD !important',
                  transition: 'background 0.2s ease',
                },
                transition: 'background 0.2s ease',
              },
              '& .MuiDataGrid-footerContainer': {
                backgroundColor: '#fff',
                borderTop: '1px solid rgba(224, 224, 224, 0.5)',
              },
               // Scrollbar visible
               '& .MuiDataGrid-virtualScroller': {
                 overflow: 'auto',
               },
             }}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    sla: false // ✅ Ocultar columna SLA desde el inicio
                  }
                }
              }}
          />
        )}
      </Paper>
      
       {/* Info de paginación y selección */}
      {!loading && (
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Mostrando {tickets.length} de {pagination.total} tickets
            {bandejaActiva && ` (filtrado por: ${bandejas.find(b => b.id === bandejaActiva)?.titulo || bandejaActiva})`}
          </Typography>
          
          {(esAgente || esAdmin) && selectedTickets.length > 0 && (
            <Typography variant="caption" color="primary" fontWeight="medium">
              {selectedTickets.length} ticket(s) seleccionado(s)
            </Typography>
          )}
        </Box>
      )}
      
      {!loading && tickets.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No se encontraron tickets con los filtros seleccionados.
        </Alert>
      )}
      
      {/* Dialog Cambiar Estado */}
      <Dialog 
        open={mostrarCambiarEstado} 
        onClose={() => {
          setMostrarCambiarEstado(false);
          setTicketSeleccionado(null);
          setNuevoEstado('');
          setErrorAccion('');
        }}
      >
        <DialogTitle>Cambiar Estado</DialogTitle>
        <DialogContent>
          {ticketSeleccionado && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ticket: {ticketSeleccionado.numero}
            </Typography>
          )}
          {errorAccion && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorAccion('')}>
              {errorAccion}
            </Alert>
          )}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Nuevo Estado</InputLabel>
            <Select
              value={nuevoEstado}
              onChange={(e) => {
                setNuevoEstado(e.target.value);
                setErrorAccion(''); // Limpiar error al cambiar selección
              }}
              label="Nuevo Estado"
            >
              <MenuItem value={ESTADOS_VISIBLES.ABIERTO}>
                {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.ABIERTO]}
              </MenuItem>
              <MenuItem value={ESTADOS_VISIBLES.EN_PROGRESO}>
                {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.EN_PROGRESO]}
              </MenuItem>
              <MenuItem value={ESTADOS_VISIBLES.COMPLETADO}>
                {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.COMPLETADO]}
              </MenuItem>
              <MenuItem value={ESTADOS_VISIBLES.PRUEBA}>
                {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.PRUEBA]}
              </MenuItem>
              <MenuItem value={ESTADOS_VISIBLES.CERRADO}>
                {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.CERRADO]}
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setMostrarCambiarEstado(false);
              setTicketSeleccionado(null);
              setNuevoEstado('');
              setErrorAccion('');
            }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleAplicarCambioEstado} 
            variant="contained" 
            disabled={!nuevoEstado || actionsLoading}
          >
            {actionsLoading ? 'Aplicando...' : 'Aplicar'}
          </Button>
       </DialogActions>
      </Dialog>
      
      {/* Dialog Cambiar Estado Masivo */}
      <Dialog 
        open={mostrarCambiarEstadoMasivo} 
        onClose={() => {
          if (!cambiandoEstadoMasivo) {
            setMostrarCambiarEstadoMasivo(false);
            setNuevoEstadoMasivo('');
            setErrorCambioMasivo('');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cambiar Estado Masivo</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cambiar estado de {selectedTickets.length} ticket(s) seleccionado(s)
          </Typography>
          
          {errorCambioMasivo && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorCambioMasivo('')}>
              {errorCambioMasivo}
            </Alert>
          )}
          
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Nuevo Estado</InputLabel>
            <Select
              value={nuevoEstadoMasivo}
              onChange={(e) => {
                setNuevoEstadoMasivo(e.target.value);
                setErrorCambioMasivo(''); // Limpiar error al cambiar selección
              }}
              label="Nuevo Estado"
              disabled={cambiandoEstadoMasivo}
            >
              <MenuItem value={ESTADOS_VISIBLES.ABIERTO}>
                {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.ABIERTO]}
              </MenuItem>
              <MenuItem value={ESTADOS_VISIBLES.EN_PROGRESO}>
                {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.EN_PROGRESO]}
              </MenuItem>
              <MenuItem value={ESTADOS_VISIBLES.COMPLETADO}>
                {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.COMPLETADO]}
              </MenuItem>
              <MenuItem value={ESTADOS_VISIBLES.PRUEBA}>
                {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.PRUEBA]}
              </MenuItem>
              <MenuItem value={ESTADOS_VISIBLES.CERRADO}>
                {LABELS_ESTADOS_VISIBLES[ESTADOS_VISIBLES.CERRADO]}
              </MenuItem>
            </Select>
          </FormControl>
          
          {/* Resumen de tickets seleccionados */}
          {selectedTickets.length > 0 && (
            <Box sx={{ mt: 3, maxHeight: 200, overflow: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom>
                Tickets seleccionados:
              </Typography>
              <Stack spacing={1}>
                {tickets
                  .filter(ticket => selectedTickets.includes(ticket.id))
                  .slice(0, 10) // Mostrar máximo 10 para no saturar
                  .map(ticket => (
                    <Box key={ticket.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={ticket.numero}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                        {ticket.titulo}
                      </Typography>
                      <EstadoBadge status={ticket.status} fontSize="0.875rem" />
                    </Box>
                  ))}
                {selectedTickets.length > 10 && (
                  <Typography variant="caption" color="text.secondary">
                    ... y {selectedTickets.length - 10} más
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setMostrarCambiarEstadoMasivo(false);
              setNuevoEstadoMasivo('');
              setErrorCambioMasivo('');
            }}
            disabled={cambiandoEstadoMasivo}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleAplicarCambioEstadoMasivo} 
            variant="contained" 
            disabled={!nuevoEstadoMasivo || cambiandoEstadoMasivo || selectedTickets.length === 0}
          >
            {cambiandoEstadoMasivo ? 'Aplicando...' : `Aplicar a ${selectedTickets.length} ticket(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BandejasAgenteComponent;
